import { PartnerJourney } from "@/components/dealer/PartnerJourney";
import { PricingSetupTask } from "@/components/dealer/PricingSetupTask";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCustomerView } from "@/context/CustomerViewContext";
import { formatApiErrorDetail } from "@/lib/errors";
import { formatMoney } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Loader2, AlertTriangle, Clock, Package, FlaskConical, FolderKanban, Activity,
  Search, FolderPlus, ShoppingCart, Ship,
} from "lucide-react";
import { DealerJourney } from "@/components/DealerJourney";

const ATTENTION_ICON = { 1: AlertTriangle, 2: Clock, 3: Activity };
const labelCls = "text-xs font-semibold uppercase tracking-[0.2em] text-charcoal";

export default function DealerDashboard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const { customerView } = useCustomerView();

  useEffect(() => {
    setData(null);
    setError(false);
    api
      .get(`/dashboard/dealer${customerView ? "?customer_view=1" : ""}`)
      .then((res) => setData(res.data))
      .catch((e) => {
        setError(true);
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      });
  }, [customerView]);

  if (error) {
    return (
      <Card className="border-border bg-white" data-testid="dashboard-error">
        <CardContent className="p-16 text-center text-sm text-muted-foreground">Dashboard unavailable — please retry.</CardContent>
      </Card>
    );
  }
  if (!data) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center" data-testid="dashboard-loading">
        <Loader2 className="h-6 w-6 animate-spin text-terracotta" />
      </div>
    );
  }

  const { attention, tiles, journey, projects = [] } = data;
  const activeProjects = Object.values(tiles.projects_by_stage || {}).reduce((a, b) => a + b, 0);

  const cards = [
    { testid: "tile-projects", icon: FolderKanban, value: activeProjects, label: "Active projects", to: "/dealer/projects" },
    { testid: "tile-reservations", icon: Clock, value: tiles.active_reservations, label: "Active reservations", sub: `${tiles.active_reservation_boxes} boxes on hold`, to: "/dealer/reservations" },
    ...(!customerView ? [{ testid: "tile-samples-progress", icon: FlaskConical, value: tiles.samples_in_progress ?? 0, label: "Samples in progress", sub: "requested or on the way", to: "/dealer/samples" }] : []),
    { testid: "tile-open-orders", icon: Package, value: tiles.open_orders, label: "Open orders", sub: `${formatMoney(tiles.open_orders_total)}${customerView ? " retail" : ""}`, to: "/dealer/orders" },
    { testid: "tile-available-now", icon: Ship, value: `${(tiles.florida_available_sqft ?? 0).toLocaleString()} sq ft`, label: "Available now", sub: `${tiles.florida_available_boxes ?? 0} boxes in Florida`, to: "/dealer/discover" },
    { testid: "tile-arriving-soon", icon: Ship, value: `${(tiles.arriving_soon_sqft ?? 0).toLocaleString()} sq ft`, label: "Arriving soon", sub: `${tiles.arriving_soon_boxes ?? 0} boxes en route to Miami`, to: "/dealer/discover" },
  ];

  const actions = [
    { testid: "qa-browse", icon: Search, label: "Browse products", to: "/dealer/discover" },
    { testid: "qa-start-project", icon: FolderPlus, label: "Start project", to: "/dealer/projects" },
    ...(!customerView ? [{ testid: "qa-request-samples", icon: FlaskConical, label: "Request samples", to: "/dealer/samples" }] : []),
    { testid: "qa-place-order", icon: ShoppingCart, label: "Place order", to: "/dealer/discover" },
    ...(!customerView ? [{ testid: "qa-container-order", icon: Ship, label: "Full Container order", to: "/dealer/container-order" }] : []),
  ];

  return (
    <div data-testid="dealer-dashboard">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Overview</p>
      <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">
        {customerView ? "Project status" : "Dashboard"}
      </h1>

      {!customerView && <PricingSetupTask />}
      {!customerView && tiles.partner_status && <PartnerJourney />}

      <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3" data-testid="dashboard-tiles">
        {cards.map((c) => (
          <Card key={c.testid} data-testid={c.testid} onClick={() => navigate(c.to)}
            className="cursor-pointer border-border bg-white transition-all duration-200 hover:-translate-y-[1px] hover:shadow-sm">
            <CardContent className="p-5">
              <c.icon className="h-4 w-4 text-terracotta" />
              <p className="mt-3 font-display text-3xl font-light text-charcoal" data-testid={`${c.testid}-value`}>{c.value}</p>
              <p className="text-xs text-muted-foreground">{c.label}</p>
              {c.sub && <p className="mt-1 text-xs text-warmgrey">{c.sub}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="quick-actions">
        {actions.map((a) => (
          <Button key={a.testid} data-testid={a.testid} onClick={() => navigate(a.to)}
            className="h-12 bg-terracotta text-sm text-white transition-colors hover:bg-terracotta-hover">
            <a.icon className="mr-2 h-4 w-4" /> {a.label}
          </Button>
        ))}
      </div>

      {!customerView && projects.length > 0 && (
        <div className="mt-8" data-testid="dealer-projects-strip">
          <p className={labelCls}>Active projects</p>
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {projects.map((p, i) => (
              <Card key={p.id} data-testid={`project-card-${i}`} onClick={() => navigate(`/dealer/projects/${p.id}`)}
                className="cursor-pointer border-border bg-white transition-all duration-200 hover:-translate-y-[1px] hover:shadow-sm">
                <CardContent className="p-4">
                  <p className="truncate text-sm font-medium text-charcoal">{p.name}</p>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">{p.client_name || "—"}</p>
                  <p className="mt-2 text-xs text-warmgrey">
                    {[p.project_type, p.projected_sqft ? `${p.projected_sqft.toLocaleString()} sq ft` : null, p.expected_timeframe]
                      .filter(Boolean).join(" · ") || "—"}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2 text-[10px] font-semibold uppercase tracking-wider">
                    {p.samples > 0 && <span className="rounded-full bg-sand px-2 py-0.5 text-warmgrey">{p.samples} sample{p.samples > 1 ? "s" : ""}</span>}
                    {p.has_active_reservation && <span className="rounded-full bg-sand px-2 py-0.5 text-terracotta">Active hold</span>}
                    {p.orders > 0 && <span className="rounded-full bg-sand px-2 py-0.5 text-warmgrey">{p.orders} order{p.orders > 1 ? "s" : ""}</span>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <DealerJourney journey={journey} />

      {attention.length > 0 && (
        <div className="mt-8" data-testid="attention-rail">
          <p className={labelCls}>Needs your attention</p>
          <div className="mt-3 space-y-2">
            {attention.map((a, i) => {
              const Icon = ATTENTION_ICON[a.priority] || Activity;
              return (
                <Card key={i} className="cursor-pointer border-border bg-white transition-all duration-200 hover:-translate-y-[1px] hover:shadow-sm" data-testid={`attention-${a.key}-${i}`} onClick={() => navigate(a.link)}>
                  <CardContent className="flex items-center gap-4 p-4">
                    <Icon className={`h-4 w-4 shrink-0 ${a.priority === 1 ? "text-status-sold" : "text-terracotta"}`} />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-charcoal">{a.title}</p>
                      <p className="text-xs text-muted-foreground">{a.detail}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
}
